import { CSRF_HEADER } from "@lms/backend/lib/csrf-constants";
import { getStoredAccessToken } from "@/lib/auth-token-storage";
import { apiUrl } from "@/services/http/api-url";
import { fetchCsrfToken, invalidateCsrfCache } from "@/services/http/csrf-token";
import { ensureFreshAccessToken, handleUnauthorized } from "@/services/http/session-refresh";
import { reportTermsRefusal } from "@/services/http/terms-required";

export { fetchCsrfToken, invalidateCsrfCache };

/**
 * SPA-7: only a CSRF refusal is worth re-sending with a fresh token (as the axios client does). A
 * real FORBIDDEN (role, ownership) would just be posted twice. Reads a clone, so the caller can
 * still read the body.
 */
async function isCsrfRefusal(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: unknown } };
    return body?.error?.code === "CSRF_INVALID";
  } catch {
    return false;
  }
}

/**
 * Authenticated fetch: API-base URL (C1), Bearer token, credentials, CSRF on mutations,
 * one retry on a CSRF_INVALID 403, and the shared 401 → refresh → retry-once contract (C2).
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path);
  const method = (init.method ?? "GET").toUpperCase();
  const callerHeaders = new Headers(init.headers);
  const callerSetAuth = callerHeaders.has("Authorization");

  const isMutating = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  const csrfExempt =
    url.includes("/auth/login") ||
    url.includes("/auth/oauth-complete") ||
    url.includes("/auth/refresh");

  async function send(withFreshCsrf: boolean): Promise<Response> {
    const requestHeaders = new Headers(callerHeaders);
    const accessToken = getStoredAccessToken();
    if (accessToken && !callerSetAuth) {
      requestHeaders.set("Authorization", `Bearer ${accessToken}`);
    }
    if (isMutating && !csrfExempt && !callerHeaders.has(CSRF_HEADER)) {
      requestHeaders.set(CSRF_HEADER, await fetchCsrfToken(withFreshCsrf));
    }

    if (
      init.body != null &&
      !(init.body instanceof FormData) &&
      typeof init.body === "string" &&
      !requestHeaders.has("Content-Type")
    ) {
      requestHeaders.set("Content-Type", "application/json");
    }

    return fetch(url, {
      ...init,
      credentials: "include",
      headers: requestHeaders,
    });
  }

  async function sendWithCsrfRetry(): Promise<Response> {
    const res = await send(false);
    if (
      isMutating &&
      !csrfExempt &&
      res.status === 403 &&
      !callerHeaders.has(CSRF_HEADER) &&
      (await isCsrfRefusal(res))
    ) {
      invalidateCsrfCache();
      return send(true);
    }
    return res;
  }

  if (!callerSetAuth) await ensureFreshAccessToken(url);
  let res = await sendWithCsrfRetry();
  if (res.status === 401 && !callerSetAuth && (await handleUnauthorized(url))) {
    res = await sendWithCsrfRetry();
  }
  await reportTermsRefusal(res);
  return res;
}
